// Funnel v2 types — food/appetite for P6
export type FoodSource =
  // 2026-04-29: 'cruda' eliminada del UI (era duplicado conceptual de BARF).
  // Añadidas 'mixta' (50% comida natural + 50% pienso) y reactivada
  // 'comida_humeda' como opción visible. El resto se mantiene como valores
  // estables para que leads/DB rows antiguos sigan deserializando.
  | 'pienso_seco'
  | 'comida_humeda'
  | 'comida_cocinada'
  | 'casera'
  | 'barf'
  | 'natural_cocinada'
  | 'mixta'
  | 'deshidratada'
  // Legacy value retained for backward compatibility with DB rows
  // saved before the option was renamed (2026-04-27).
  | 'cruda'
  | 'premios_sobras'
  // Explicit "don't know" — triggers standard transition schedule.
  | 'no_lo_se';

export type AppetiteType = 'come_todo' | 'casi_siempre' | 'a_veces_cansa' | 'selectivo';

// Pathology tier classification for P8
export type PathologyTier = 1 | 2 | 3;

export interface PathologyMeta {
  key: string;
  label: string;
  tier: PathologyTier;
  emoji?: string;
  icon?: string;
}

// Enums
export enum AnimalType {
  PERRO = 'perro',
  GATO = 'gato',
}

export enum AgeCategory {
  CACHORRO = 'cachorro',
  ADULTO = 'adulto',
  SENIOR = 'senior',
}

export enum ActivityLevel {
  BAJO = 'bajo',
  MEDIO = 'medio',
  ALTO = 'alto',
}

export enum BodyCondition {
  DELGADO = 'delgado',
  IDEAL = 'ideal',
  SOBREPESO = 'sobrepeso',
}

export enum DietType {
  COMPLETA = 'completa',
  MIXTA = 'mixta',
}

// Pathologies
export enum Pathology {
  NINGUNA = 'ninguna',
  ALERGIAS_ALIMENTARIAS = 'alergias_alimentarias',
  SENSIBILIDAD_DIGESTIVA = 'sensibilidad_digestiva',
  SOBREPESO_OBESIDAD = 'sobrepeso_obesidad',
  PROBLEMAS_ARTICULARES = 'problemas_articulares',
  PIEL_PELO = 'piel_pelo',
  DENTAL = 'dental',
  RENAL = 'renal',
  HEPATICA = 'hepatica',
  DIABETES = 'diabetes',
  PANCREATICA = 'pancreatica',
  // Reincorporadas tras la review de Codex 2026-04-28: en P8 son
  // seleccionables (tier 1) pero no había enum y mapCatalogKeyToPathologyEnum
  // devolvía null, perdiendo silenciosamente la condición. Backend
  // schemas/dog.py debe sincronizarse con estos valores.
  CANCER = 'cancer',
  EPILEPSIA = 'epilepsia',
  CARDIOPATIAS = 'cardiopatias',
  OTRA = 'otra',
}

// Proteins
export enum Protein {
  NINGUNA = 'ninguna',
  POLLO = 'pollo',
  TERNERA = 'ternera',
  CORDERO = 'cordero',
  PESCADO = 'pescado',
  PAVO = 'pavo',
  CONEJO = 'conejo',
  CERDO = 'cerdo',
  PATO = 'pato',
}

export enum Gender {
  MACHO = 'macho',
  HEMBRA = 'hembra',
}

// Form Data
export interface DogFormData {
  // Step 1 — P1Welcome
  animalType?: AnimalType;
  nombre: string;

  // Step 2 — P2DatosBasicos
  gender?: Gender;
  ageCategory?: AgeCategory;
  ageYears?: number;
  ageMonths?: number;
  ageTotalMonths: number; // total age in months
  isNeutered?: boolean;

  // Step 3 — P3Raza
  raza: string;
  breed_code?: string;

  // Step 4 — P4Peso
  peso: number; // kg
  bodyCondition: BodyCondition;

  // Step 5 — P5Actividad
  activityLevel: ActivityLevel;
  /** True when the user explicitly picked a level in P5. Distinguishes the
   *  global default ('medio') from a conscious choice when navigating back —
   *  without it, someone who picked Equilibrado and goes back would see
   *  nothing selected. */
  activityLevelChosen?: boolean;

  // Step 6 — P6ComidaApetito
  currentFood?: FoodSource[];
  appetiteType?: AppetiteType;
  /** Initialised here from the food type (skip transition → 'full'); the user
   *  can still re-pick it on Step 12 (ComoEmpezar). */
  subscriptionStart?: 'transition' | 'full';

  // Step 7 — P7Alergias
  proteinAllergies: Array<Protein | string>;
  /** Gate answer for P7: 'si' reveals the allergen grid, 'no' means no known
   *  allergies (equivalent to Protein.NINGUNA). Mirrors healthGateAnswer in P8. */
  allergyGateAnswer?: 'si' | 'no';
  /** UI-level allergen option keys selected in P7. Several UI choices can map
   *  to the same blocking protein, so this preserves what the user picked. */
  allergenKeys?: string[];

  // Step 8 — P8Salud
  pathologies: Pathology[];
  healthGateAnswer?: 'si' | 'no' | 'no_se';
  subPathologies?: Record<string, string[]>;
  /** Free text when the user picks "Otra" in P8 — describes the unlisted
   *  condition so customer support can review it. */
  otherPathologyText?: string;

  // Step 9 — P9Reveal: calculation screen, no persisted fields of its own.

  // Step 10 — Step10RecetasV2
  selectedRecipes?: string[];
  sizeChoice?: Record<string, 'auto' | '400g' | '800g'>;
  globalSizeChoice?: '400g' | '800g';
  recipeEnergyDistribution?: Record<string, number>;
  /**
   * Set to true when checkout failed due to recipe stock/availability and the
   * user was bounced back to step 10. Prevents the auto-select-all default and
   * triggers an in-page warning. Cleared when the user continues from step 10.
   */
  recipeStockBounced?: boolean;

  // Step 11 — Step11TuPlan
  dietType: DietType;
  dailyKcal?: number;
  dailyGrams?: number;

  // Step 12 — Step12ComoEmpezar
  initiationPackDistribution?: Record<string, number>;
  recommendedInitiationPackDistribution?: Record<string, number>;
  // Monthly cans per product from the latest plan; the checkout pack editor keeps them
  // off the editable margin when inventory_rules.stock_reserve_scope covers the month.
  monthlyPlanDistribution?: Record<string, number>;
  // True once the user manually edits the pack quantities in the checkout: the
  // submit then treats initiationPackDistribution as authoritative instead of
  // re-fetching the backend's recommended cans. Reset whenever the plan is
  // recalculated (recipe/parameter change).
  packDistributionEdited?: boolean;
  initiationPackPrice?: number;
  monthlyPlanPrice?: number;
  monthlyPackSummary?: import('../api/calculate').PackSummaryResponse;
  initiationPackSummary?: import('../api/calculate').PackSummaryResponse;
  packConservation?: import('../api/calculate').PackConservationResponse;

  // Step 13 — Step12Extras
  selectedSupplements?: string[];
  selectedSupplementVariantIds?: string[];
  selectedSupplementNames?: string[];
  supplementsOneOffTotal?: number;

  // Step 14 — Step13Checkout
  calculationId?: string;
  email?: string;
  acceptsMarketing: boolean;
  /** Shipping fields populated when shipping_rules.enabled. */
  shippingCountry?: string;
  shippingPostalCode?: string;
  shippingZoneId?: string | null;
  shippingPrice?: number;
  /** geoip = pre-filled from country gate; manual = user changed dropdown;
   *  postal_code = zone derived from typed CP. Source of truth in `types/config.ts`. */
  shippingDetectionSource?: import('./config').ShippingDetectionSource;

  // Origin ecommerce hint = canal efectivo resuelto por el backend en /app-config.
  // El front lo reenvía en /calculate* para que el cálculo resuelva el MISMO canal
  // que el catálogo; si falta, el backend cae al detect_fallback.
  platform?: 'shopify' | 'prestashop';

  // Calculadora informativa (modo kcal): salta el bloqueo/filtrado clínico del checkout.
  informative?: boolean;
}

// Recipe/Product
export interface RecipeVariant {
  /** Canonical product.id (UUID) — the funnel's channel-agnostic recipe key
   *  used for distribution/checkout. */
  productId: string;
  /** Real Shopify variant id for this size (may be '' for PrestaShop-only
   *  products). NOT the funnel key — kept only for `mascotas_json` (Shopify support). */
  shopifyVariantId: string;
  priceEur: number;
  /** Out of stock for THIS size in the effective channel (from the size product's
   *  binding snapshot). The global format selector disables a size that is OOS in
   *  any selected recipe. */
  isOutOfStock?: boolean;
  /** Units available for THIS size in the effective channel (binding snapshot).
   *  Used to estimate whether the size covers the customer's plan; null = unknown. */
  stockAvailableQty?: number | null;
}

export interface Recipe {
  id: string;
  handle: string;
  name: string;
  proteinType: Protein;
  proteinTypes?: Protein[];
  allergens?: string[];
  kcalPer100g: number;
  servingSize: number; // gramos
  pricePerUnit: number; // euros
  imageUrl: string;
  tags: string[];
  nutritionFacts: {
    protein: number; // %
    fat: number; // %
    fiber: number; // %
    moisture: number; // %
  };
  ingredients: string;
  benefits: string[];
  /** Canonical product.id (UUID) — the funnel's channel-agnostic recipe key
   *  used as the key for distribution, selection, checkout payload, whitelist
   *  and OOS matching. */
  productId: string;
  /** Real Shopify variant id (may be '' for PrestaShop-only products). NOT the
   *  funnel key — kept only for `mascotas_json` (Shopify support). */
  shopifyVariantId: string;
  isExcluded?: boolean; // due to allergies or configured clinical rules
  excludedBy?: string[]; // allergens/pathologies that caused the exclusion
  exclusionReason?: 'allergy' | 'pathology';
  isOutOfStock?: boolean; // out of stock in Shopify (shown disabled, not selectable)
  /** Units available in the effective channel (binding snapshot) for THIS product's
   *  size; null = unknown. Carried per-size into `variants[*].stockAvailableQty`. */
  stockAvailableQty?: number | null;

  // Optional new fields for cocinada catalog (Task 15)
  recipeGroup?: string; // e.g. "pollo_adulto"
  packSizeG?: 400 | 800;
  lifeStage?: 'adult' | 'puppy' | 'all';
  variants?: {
    '400g'?: RecipeVariant;
    '800g'?: RecipeVariant;
  };
}
